import React, { useEffect, useState } from 'react';
import ApiService from '@zola-helpers/client/dist/es/http/api';
import _isEqual from 'lodash/isEqual';
import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import PayPal from './PayPal';
import paypal from './PaypalCheckout';

type PaypalButtonWrapperProps = {
  items: CartItemType[];
  amountCents: number;
  onApprove: typeof paypal.tokenizePaypalPayment;
  tokenUrl: string;
  collectionUrl: string;
};

export const PaypalButtonWrapper = ({
  items,
  amountCents,
  onApprove,
  tokenUrl,
  collectionUrl,
}: PaypalButtonWrapperProps) => {
  const [extendedItems, setExtendedItems] = useState<
    (CartItemType & { cash_fund: boolean })[] | null
  >(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        loading ||
        (extendedItems &&
          _isEqual(
            extendedItems.map((x) => x.id),
            items.map((x) => x.id)
          ))
      ) {
        return;
      }

      setLoading(true);
      Promise.all(
        items.map((item) => {
          if (item.isMerchandise || item.isInvite) {
            return Promise.resolve({ ...item, cash_fund: false });
          }

          const [uuid] = (item.itemLink || '').split('/').slice(-1);

          if (!uuid) {
            throw new Error(`itemLink: ${item.itemLink} is malformed`);
          }

          return ApiService.get<{ data: { cash_fund: boolean } }>(`${collectionUrl}/${uuid}`)
            .then((res) => ({
              ...item,
              cash_fund: res.data.cash_fund,
            }))
            .catch(() => ({ ...item, cash_fund: false })); // assume non-cashfund if BE 404s
        })
      )
        .then((results) => {
          setLoading(false);
          setExtendedItems(results);
        })
        .catch((e) => {
          throw e;
        });
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [items, extendedItems, loading, collectionUrl]);

  if (loading || !extendedItems) {
    return null;
  }

  const hasNonCashFunds = !!extendedItems.find((item) => !item.cash_fund);

  return (
    <PayPal.Buttons
      amountCents={amountCents}
      onApprove={onApprove}
      showPayLater={hasNonCashFunds}
      tokenUrl={tokenUrl}
    />
  );
};

export default PaypalButtonWrapper;
